import type { CultivarComparison, CultivarSalesProfile } from '../types/cultivar';

/**
 * A cultivar's talking points, between the stored shape and the form that
 * edits them. Lists are edited one per line.
 */

/** Whether there is anything to show. An all-blank profile reads as none. */
export function profileHasContent(profile: CultivarSalesProfile | null | undefined): profile is CultivarSalesProfile {
  if (!profile) return false;
  return Boolean(profile.summary || profile.fruit || profile.season || profile.tree || profile.growing)
    || (profile.best_for?.length ?? 0) > 0
    || (profile.selling_points?.length ?? 0) > 0
    || (profile.watch_outs?.length ?? 0) > 0
    || (profile.compared_with?.length ?? 0) > 0;
}

export interface ProfileDraft {
  summary: string;
  fruit: string;
  season: string;
  tree: string;
  growing: string;
  best_for: string;
  selling_points: string;
  watch_outs: string;
  compared_with: CultivarComparison[];
}

const lines = (text: string) => text.split('\n').map((l) => l.replace(/^[\s•*-]+/, '').trim()).filter(Boolean);
const text = (v: string) => (v.trim() ? v.trim() : null);

export function toDraft(profile: CultivarSalesProfile | null | undefined): ProfileDraft {
  return {
    summary: profile?.summary ?? '',
    fruit: profile?.fruit ?? '',
    season: profile?.season ?? '',
    tree: profile?.tree ?? '',
    growing: profile?.growing ?? '',
    best_for: (profile?.best_for ?? []).join('\n'),
    selling_points: (profile?.selling_points ?? []).join('\n'),
    watch_outs: (profile?.watch_outs ?? []).join('\n'),
    compared_with: (profile?.compared_with ?? []).map((c) => ({ ...c })),
  };
}

/**
 * The draft as a profile to save. `meta` is where the text came from (sources,
 * model, when), carried through unchanged so an edit does not lose it. A
 * comparison keeps its cultivar link only while its name is unchanged.
 */
export function fromDraft(draft: ProfileDraft, meta: CultivarSalesProfile | null | undefined): CultivarSalesProfile {
  const linked = new Map((meta?.compared_with ?? []).map((c) => [c.name.trim().toLowerCase(), c.cultivar_id]));
  return {
    summary: text(draft.summary),
    fruit: text(draft.fruit),
    season: text(draft.season),
    tree: text(draft.tree),
    growing: text(draft.growing),
    best_for: lines(draft.best_for),
    selling_points: lines(draft.selling_points),
    watch_outs: lines(draft.watch_outs),
    compared_with: draft.compared_with
      .map((c) => ({ name: c.name.trim(), difference: c.difference.trim() }))
      .filter((c) => c.name && c.difference)
      .map((c) => ({ ...c, cultivar_id: linked.get(c.name.toLowerCase()) ?? null })),
    sources: meta?.sources ?? [],
    confidence: meta?.confidence ?? null,
    researched_at: meta?.researched_at ?? null,
    researched_by_model: meta?.researched_by_model ?? null,
  };
}
